import { z } from 'zod';
import { DAY_PATTERN, isDay } from './range.js';
export const JOB_TYPES = ['print', 'copy', 'scan', 'fax'];
/**
 * Ce qu'on peut filtrer par type : chaque type seul, et **impressions + copies** ensemble
 * (24.09.2026). En principe, seules les impressions et les copies coûtent — c'est le total
 * facturable qu'on veut obtenir d'un seul filtre, sans additionner deux exports à la main.
 */
export const TYPE_FILTERS = [...JOB_TYPES, 'print-copy'];
/** Colonnes sur lesquelles on peut trier. Volontairement closes : pas de tri arbitraire. */
export const SORT_KEYS = [
    'date',
    'user',
    'device',
    'establishment',
    'costCenter',
    'pages',
];
/** Borne supérieure explicite sur toute liste (Charte §1). Les journaux sont une liste grande. */
export const JOBS_PAGE_MAX = 200;
export const JOBS_PAGE_DEFAULT = 50;
/** Un jour du calendrier suisse, `AAAA-MM-JJ` — voir `range.ts`. */
const daySchema = z.string().regex(DAY_PATTERN).refine(isDay, 'Jour inexistant');
/** Les dates d'une requête : un mois entier, ou « du … au … » compris (VUE-16). */
export const dateFilterSchema = z.object({
    /** « AAAA-MM ». Prioritaire sur `from`/`to` : les liens d'avant VUE-16 l'utilisent. */
    period: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .optional(),
    /** Premier jour, compris. */
    from: daySchema.optional(),
    /** Dernier jour, **compris** — « du 1ᵉʳ au 31 août ». */
    to: daySchema.optional(),
});
export const jobsQuerySchema = dateFilterSchema.extend({
    /** Recherche libre : personne, appareil, nom du document, centre de coût. */
    q: z.string().trim().min(1).max(120).optional(),
    type: z.enum(TYPE_FILTERS).optional(),
    /**
     * Établissement du TRAVAIL — celui de l'imprimante (D14). Ne peut que restreindre le
     * périmètre, jamais l'élargir.
     */
    establishment: z.string().trim().min(1).max(64).optional(),
    /**
     * Établissement de la PERSONNE — « les travaux des profs de l'ECGF faits chez moi ».
     * Restreint aussi, et seulement : le périmètre reste celui de l'imprimante.
     */
    userEstablishment: z.string().trim().min(1).max(64).optional(),
    /** Un centre de coût précis (VUE-15) — la refacturation des impressions privées. */
    costCenter: z.string().trim().min(1).max(200).optional(),
    /** Un utilisateur précis — posé d'un clic sur son nom dans le tableau. */
    user: z.string().trim().min(1).max(200).optional(),
    /** Un appareil précis — même geste. */
    device: z.string().trim().min(1).max(200).optional(),
    /** Ne garder que les travaux comportant au moins une page couleur. */
    color: z
        .enum(['1', 'true', 'oui'])
        .optional()
        .transform((v) => v !== undefined),
    sort: z.enum(SORT_KEYS).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(JOBS_PAGE_MAX).default(JOBS_PAGE_DEFAULT),
    offset: z.coerce.number().int().min(0).default(0),
});
